import { FC, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import { IconUpload } from '@tabler/icons-react'
import { JumpingDots } from '../../atoms/JumpingDots'
import Loader from '../../atoms/Loader'
import Typography from '../../atoms/Typography'
import { GradientBorderButton } from '../../atoms/buttons/GradientBorderButton'
import { api } from '../../../api/root'
import { DataRoomDocument, DataRoomFolder } from '../../../types/projects'
import uploadFileToDataRoomSvg from 'src/assets/img/uploadFileToDataRoom.svg'
import { SUPPORTED_FORMATS_LABEL } from './DataRoomFileDropzone'
import { MAX_FILE_SIZE_BYTES } from 'src/enviroment/constants/constants'
import { formatFileSize } from '../../../enviroment/lib/formatters/format-file-size'
import { DataRoomDocumentCard } from './DataRoomDocumentCard'
import { PendingFileCard } from './PendingFileCard'
import FolderBreadcrumbs from '../../molecules/FolderBreadcrumbs'
import FolderRow from '../../molecules/FolderRow'

interface DataRoomDocumentsListProps {
  projectId: string
  folderPath: string | null
  isFoldersLoading: boolean
  fileCount: number
  folders: DataRoomFolder[]
  onSelectFolder: (path: string | null) => void
  onSelectDocument: (doc: DataRoomDocument) => void
  actionsEnabled: boolean
  pendingFiles: File[]
  onUpload?: () => void
  selectedIds: Set<string>
  onToggleSelect: (id: string) => void
  isDeleting: boolean
}

const UploadArea = ({ onUpload, hasFiles }: { onUpload?: () => void; hasFiles: boolean }) => (
  <div className="flex flex-1 flex-col items-center justify-center gap-4 py-8">
    {!hasFiles && (
      <>
        <img src={uploadFileToDataRoomSvg} alt="Upload File illustration" className="h-auto max-w-full" />
        <Typography variant="xl-bold" className="text-gray-50">
          No files here yet.
        </Typography>
      </>
    )}
    {onUpload && (
      <GradientBorderButton gradientSize="sm" textSize="sm" onClick={onUpload}>
        <IconUpload className="h-4 w-4" />
        {hasFiles ? 'Upload another file' : 'Upload a file'}
      </GradientBorderButton>
    )}
    <div className="flex flex-col items-center gap-1 text-center">
      <Typography variant="xs" className="text-gray-300">
        Supported formats: {SUPPORTED_FORMATS_LABEL}
      </Typography>
      <Typography variant="xs" className="text-gray-300">
        Maximum file size: {formatFileSize(MAX_FILE_SIZE_BYTES)}
      </Typography>
    </div>
  </div>
)

const DataRoomDocumentsList: FC<DataRoomDocumentsListProps> = ({
  projectId,
  folderPath,
  isFoldersLoading,
  fileCount,
  folders,
  onSelectFolder,
  onSelectDocument,
  actionsEnabled,
  pendingFiles,
  onUpload,
  selectedIds,
  onToggleSelect,
  isDeleting,
}) => {
  const {
    data: documentsData,
    isFetchingNextPage,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = api.project.dataRoom.documents.useInfiniteQuery({ projectId, folderPath })

  const { ref: inViewRef, inView } = useInView({ threshold: 0, rootMargin: '100px' })

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  const documents = documentsData?.pages.flatMap((p) => p.documents) ?? []

  const renderContent = () => {
    if (isLoading || isFoldersLoading) {
      return (
        <div className="flex h-full items-center justify-center">
          <Loader className="text-light-green-500" />
        </div>
      )
    }

    const hasFiles = documents.length > 0 || pendingFiles.length > 0

    return (
      <>
        {folders.length > 0 && (
          <div className="mb-3 grid shrink-0 grid-cols-3 gap-3">
            {folders.map((folder) => (
              <FolderRow
                key={folder.id}
                name={folder.name}
                onClick={() => onSelectFolder(folder.path)}
                trailing={
                  <Typography variant="xs" className="shrink-0 font-medium text-gray-200">
                    {folder.documentCount}
                  </Typography>
                }
              />
            ))}
          </div>
        )}

        {hasFiles && (
          <div className="grid shrink-0 grid-cols-3 gap-3">
            {pendingFiles.map((file, index) => (
              <PendingFileCard key={`pending-${index}`} file={file} isUploading />
            ))}
            {documents.map((doc) => (
              <DataRoomDocumentCard
                key={doc.id}
                doc={doc}
                onClick={() => onSelectDocument(doc)}
                actionsEnabled={actionsEnabled}
                isSelected={selectedIds.has(doc.id)}
                onToggleSelect={onToggleSelect}
              />
            ))}
          </div>
        )}

        {/* Outside the files block: a page can be all subfolders, and the next page still holds files */}
        {hasNextPage && (
          <div ref={inViewRef} className="mt-4 shrink-0 py-2 text-center">
            {isFetchingNextPage && <JumpingDots className="text-light-green-400" />}
          </div>
        )}

        <UploadArea hasFiles={hasFiles} onUpload={onUpload} />
      </>
    )
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="px-5 pt-8">
        <FolderBreadcrumbs path={folderPath} rootLabel="All Documents" variant="base-bold" onSelect={onSelectFolder} />
        {isFoldersLoading ? (
          <div className="mt-1 h-3.5 w-20 animate-pulse rounded bg-gray-700" />
        ) : (
          <Typography variant="xs" className="text-[#9CA3AF]">
            {fileCount} documents
          </Typography>
        )}
      </div>

      <div
        className={`flex min-h-0 flex-1 flex-col overflow-y-auto p-4 ${
          isDeleting ? 'pointer-events-none opacity-50' : ''
        }`}
      >
        {renderContent()}
      </div>
    </div>
  )
}

export default DataRoomDocumentsList
